/*
 * Copyright Higharc Inc. 2020
 */
import * as THREE from "three";
import { UVVertexShader } from "./UVVertexShader";

interface DepthDownsampleShaderType {
  defines: {
    DEPTH_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
    DST_DEPTH_PACKING: number; // eslint-disable-line @typescript-eslint/naming-convention
    MIN_FILTER: number; // eslint-disable-line @typescript-eslint/naming-convention
    MAX_FILTER: number; // eslint-disable-line @typescript-eslint/naming-convention
    DEPTH_IN_ALPHA: number; // eslint-disable-line @typescript-eslint/naming-convention
  };
  uniforms: {
    tDepth: THREE.IUniform;
    invSize: THREE.IUniform;
  };
  vertexShader: string;
  fragmentShader: string;
}

// A shader for downsampling a depth buffer. Can source data from either a depth texture or an RGBA-packed depth buffer.
export const DepthDownsampleShader: DepthDownsampleShaderType = {
  defines: {
    DEPTH_PACKING: 1,
    DST_DEPTH_PACKING: 1,
    MIN_FILTER: 0, // If set, the resulting values are minimums of each 2x2 pixel gather region.
    MAX_FILTER: 0, // If set, the resulting values are maximums of each 2x2 pixel gather region.
    DEPTH_IN_ALPHA: 0,
  },
  uniforms: {
    tDepth: { value: null },

    // Inverse of the source depth buffer size in pixels - can also be thought as the size of one pixel in UV space.
    invSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },
  },
  vertexShader: UVVertexShader,
  fragmentShader: `#include <packing>

        varying vec2 vUv;

        uniform sampler2D tDepth;
        uniform vec2 invSize;

        float getDepth( const in vec2 screenPosition ) {
            #if DEPTH_PACKING == 1
                return unpackRGBAToDepth( texture2D( tDepth, screenPosition ) );
            #elif DEPTH_IN_ALPHA == 1
                return texture2D( tDepth, screenPosition ).a;
            #else
                return texture2D( tDepth, screenPosition ).r;
            #endif
        }

        void main() {

            vec2 offsets[4];
            offsets[0] = vec2(-1, -1);
            offsets[1] = vec2( 1, -1);
            offsets[2] = vec2(-1,  1);
            offsets[3] = vec2( 1,  1);

            #if MIN_FILTER == 1
                float minDepth = 1.0;
            #elif MAX_FILTER == 1
                float maxDepth = 0.0;
            #else
                float depthSum = 0.0;
            #endif

            for (int i = 0; i < 4; ++i) {
                vec2 srcUv = vUv + offsets[i] * 0.5 * invSize;
                float depth = getDepth( srcUv );
                #if MIN_FILTER == 1
                    minDepth = min(depth, minDepth);
                #elif MAX_FILTER == 1
                    maxDepth = max(depth, maxDepth);
                #else
                    depthSum += depth;
                #endif
            }

            #if MIN_FILTER == 1
                float depth = minDepth;
            #elif MAX_FILTER == 1
                float depth = maxDepth;
            #else
                float depth = depthSum * 0.25;
            #endif

            #if DST_DEPTH_PACKING == 1
                gl_FragColor = packDepthToRGBA(depth);
            #elif DEPTH_IN_ALPHA == 1
                gl_FragColor = vec4(0, 0, 0, depth);
            #else
                gl_FragColor = vec4(depth, 0, 0, 0);
            #endif
        }`,
};
